import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { TerrainIcon } from './TerrainIcon';
import { TERRAIN_TYPES, TERRAIN_LABELS, TerrainType } from '@/lib/types/core/terrain';
import { cn } from '@/lib/utils';
import { Mountain } from 'lucide-react';

interface TerrainSelectorProps {
  value: TerrainType[];
  onChange: (value: TerrainType[]) => void;
  disabled?: boolean;
}

const MAX_VISIBLE_CHIPS = 5;

export const TerrainSelector: React.FC<TerrainSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<TerrainType[]>(value);
  const gridRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Sync pending selection when value changes externally
  useEffect(() => {
    if (!open) {
      setPendingSelection(value);
    }
  }, [value, open]);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (disabled) return;

    if (isOpen) {
      setPendingSelection(value);
    }
    setOpen(isOpen);
  }, [disabled, value]);

  const toggleTerrain = useCallback((terrain: TerrainType) => {
    setPendingSelection(prev =>
      prev.includes(terrain)
        ? prev.filter(t => t !== terrain)
        : [...prev, terrain]
    );
  }, []);

  const handleApply = useCallback(() => {
    onChange(pendingSelection);
    setOpen(false);
  }, [onChange, pendingSelection]);

  const handleCancel = useCallback(() => {
    setPendingSelection(value);
    setOpen(false);
  }, [value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, terrain: TerrainType) => {
    if (e.key === ' ') {
      e.preventDefault();
      toggleTerrain(terrain);
    }
  }, [toggleTerrain]);

  const handlePopoverKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
      // Return focus to trigger
      triggerRef.current?.focus();
    }
  }, [handleCancel]);

  const visibleChips = value.slice(0, MAX_VISIBLE_CHIPS);
  const overflowCount = value.length - MAX_VISIBLE_CHIPS;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild ref={triggerRef}>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex items-center gap-2 min-h-[40px] px-3 py-2 rounded-md border border-input bg-background",
            "hover:bg-accent/10 transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          aria-label="Select terrain types"
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          {value.length === 0 ? (
            <>
              <Mountain className="h-5 w-5 text-muted-foreground" />
              <span className="text-muted-foreground text-sm">Select terrain...</span>
            </>
          ) : (
            <div className="flex items-center gap-1.5 flex-wrap">
              {visibleChips.map((terrain) => (
                <div
                  key={terrain}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-adv-orange/20 text-adv-orange text-xs font-medium"
                >
                  <TerrainIcon terrain={terrain} size={14} />
                  <span className="max-w-[80px] truncate">{TERRAIN_LABELS[terrain]}</span>
                </div>
              ))}
              {overflowCount > 0 && (
                <span className="text-xs text-muted-foreground font-medium">
                  +{overflowCount} more
                </span>
              )}
            </div>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[400px] max-w-[calc(100vw-32px)] p-0"
        align="start"
        onKeyDown={handlePopoverKeyDown}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleCancel();
        }}
      >
        <div className="p-4">
          {/* Header */}
          <h4 className="text-sm font-medium text-foreground mb-3">
            Expected Terrain
          </h4>

          {/* Grid of terrain icons */}
          <TooltipProvider delayDuration={300}>
            <div
              ref={gridRef}
              className="grid grid-cols-3 sm:grid-cols-4 gap-2"
              role="group"
              aria-label="Terrain type options"
            >
              {TERRAIN_TYPES.map((terrain) => {
                const isSelected = pendingSelection.includes(terrain);
                return (
                  <Tooltip key={terrain}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => toggleTerrain(terrain)}
                        onKeyDown={(e) => handleKeyDown(e, terrain)}
                        className={cn(
                          "flex flex-col items-center justify-center p-2 rounded-lg transition-all",
                          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
                          isSelected
                            ? "bg-adv-orange text-white shadow-md"
                            : "bg-adv-darkGray text-gray-300 hover:shadow-lg hover:shadow-adv-orange/20"
                        )}
                        aria-pressed={isSelected}
                        aria-label={`${TERRAIN_LABELS[terrain]}${isSelected ? ' (selected)' : ''}`}
                      >
                        <TerrainIcon
                          terrain={terrain}
                          size={28}
                          className={cn(
                            isSelected ? "text-white" : "text-gray-300"
                          )}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {TERRAIN_LABELS[terrain]}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>

          {/* Footer with Apply button */}
          <div className="flex justify-between items-center mt-4 pt-3 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {pendingSelection.length} selected
            </span>
            <Button
              type="button"
              onClick={handleApply}
              className="px-4 py-1.5 text-sm"
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TerrainSelector;
